import type { ContractVersionView, InternalCandidate } from "@closer/shared";
import { queryOptions } from "@tanstack/react-query";

import { api } from "./apiClient";
import { tenantKey } from "./tenantQuery";

/**
 * BİRDEN ÇOK YÜZEYİN OKUDUĞU UÇLAR için sorgu seçeneği üreticileri.
 *
 * ### ⚠ Üye dizini yardımcısı BURADAN KALDIRILDI (Mega E2E BUG-002)
 *
 * `uyeDizinSorgusu` yalnız sözleşme seçicileri için vardı ve `GET /api/tenants/:id/members`
 * okuyordu; o ucun kapısı `organization.member`, yani bir ORGANİZASYON YÖNETİMİ yetkisi.
 * Sözleşmeyi hazırlayan standart kullanıcı 403 alıyor, seçici sessizce boş kalıyordu.
 * Seçiciler `sozlesmeAdaylariSorgusu`'na geçti ve yardımcının çağıranı kalmadı. Ölü hâlde
 * bırakmak, aynı yanlış kapıyı tekrar açmaya davetti — üye dizinini gerçekten YÖNETİM
 * ekranı için okuyan yüzey kendi sorgusunu kurar (`pages/organization/`).
 *
 * ### ⚠ Neden var: aynı uç, yedi ayrı anahtar
 *
 * 5 Eylül denetiminde ölçüldü. `GET /api/tenants/:id/members` birebir aynı URL ile YEDİ
 * farklı anahtar altında okunuyordu — `uyeler/onaylayici-secimi`, `uyeler/temsilci-secimi`,
 * `uyeler/inceleyici-secimi`, `uyeler/imzaci-secimi`, `uyeler/sorumlu-secimi`,
 * `uyeler/yukumluluk-sorumlusu`, `uyeler/yeniden-atama`. Aynı desen
 * `GET /api/contracts/:id/versions?limit=50` için SEKİZ anahtarla tekrarlıyordu.
 *
 * Ölçülebilir sonucu: `/app/sozlesmeler/:id/surec` açıldığında `contract.approve` +
 * `contract.reconcile` taşıyan bir kullanıcı `members` ucunu EŞ ZAMANLI İKİ KEZ çağırıyordu
 * (`ContractApprovalSection` ve `ContractReconciliationSection`, ikisi de modal dışında
 * `enabled`). Her iki dosyanın yorumu *"iki yerde iki istek değil, tek önbellek"* diyordu;
 * iddia bölüm İÇİNDE doğruydu, bölümler ARASINDA yanlıştı.
 *
 * ### ⚠ Amaç anahtara girmez
 *
 * `onaylayici-secimi` ile `imzaci-secimi` aynı veriyi okuyor; farkları veriyle değil
 * KULLANIMLA ilgili — hangi listeyi hangi ekranda süzdüğümüz. Süzme çağıranda kalıyor
 * (`.filter(...)`), anahtar yalnız "hangi veri" sorusunu cevaplıyor. Anahtara amaç
 * yazmak, önbelleği kullanım sayısı kadar parçalıyordu.
 *
 * ### ⚠ Neden `queryOptions`, çıplak anahtar üreticisi değil
 *
 * Çıplak bir `uyeListesiAnahtari(tenantId)` yalnız tekrarı kapatırdı; tip her çağıranda
 * serbest kalır ve aynı anahtarın altına farklı tipler yazılabilirdi — bu kod tabanında
 * bir kez oldu (`paylasim-baglami` anahtarı iki dosyada iki farklı tiple okunuyordu ve
 * yalnız tesadüfen çalışıyordu). `queryOptions` anahtarı, `queryFn`'i ve tipi tek nesnede
 * bağlıyor: üçü birlikte taşınıyor.
 *
 * ⚠ `enabled` BURADA YOK, bilerek: her çağıranın kapısı farklı (yetki, modal açık mı,
 * arşivde mi). Seçenekler yayılıp `enabled` çağıranda ekleniyor.
 */

/**
 * SÖZLEŞME BAĞLAMINDAKİ İÇ ADAYLAR (Mega E2E BUG-002).
 *
 * ### ⚠ Neden üye dizini değil
 *
 * Sorumlu/inceleyici/onaylayıcı/imzacı seçicileri üye dizinini okuyordu; o ucun kapısı
 * `organization.member`, yani bir ORGANİZASYON YÖNETİMİ yetkisi. Canlı ölçümde
 * `contract.edit` taşıyan standart kullanıcı 403 alıyor, seçici SESSİZCE boş çiziliyor ve
 * hazırlanan sözleşmeye kimse atanamıyordu.
 *
 * ⚠ ANAHTAR SÖZLEŞME BAŞINA: aynı çalışma alanının iki sözleşmesi aynı adayları döndürse
 * bile yanıt sözleşmeye ERİŞİMLE kazanılıyor. Ortak bir tenant anahtarı, erişimi olan bir
 * sözleşmeden alınan listeyi erişimi olmayan bir sözleşmenin ekranına taşırdı.
 *
 * ⚠ Süzgeç sunucuda: liste yalnız aktif üyelikleri taşır, çağıran ayrıca süzmez.
 */
export function sozlesmeAdaylariSorgusu(
  tenantId: string,
  contractId: string,
  /**
   * YETENEK SÜZGECİ (Mega E2E BUG-009/009b).
   *
   * ⚠ Verilirse sunucu adayları KAPININ KURALIYLA süzer: yetenek VE bu sözleşmeye kayıt
   * erişimi. Canlı ölçümde onaylayıcı seçicisi `contract.approve` taşımayan üyeleri
   * listeliyordu; seçilince `POST /contracts/:id/approvals` 403 dönüyordu.
   *
   * ⚠ Verilmezse liste süzülmez — "organizasyonda kim var" diye soran seçiciler (iç
   * sorumlu, görev devri) için doğru olan bu. Süzgeci her yere uygulamak, sorumlu
   * atanabilecek kişileri de daraltırdı.
   */
  capability?: "contract.approve" | "contract.review" | "contract.sign" | "contract.reconcile",
) {
  const yol = capability
    ? `/api/contracts/${contractId}/internal-candidates?capability=${capability}`
    : `/api/contracts/${contractId}/internal-candidates`;
  return queryOptions({
    /** ⚠ Yetenek ANAHTARIN parçası: farklı yetenekle süzülen liste farklı bir veridir. */
    queryKey: tenantKey(tenantId, "contract", contractId, "ic-adaylar", capability ?? "hepsi"),
    queryFn: async () => (await api<{ candidates: InternalCandidate[] }>(yol, { tenantId })).data,
  });
}

/**
 * Sözleşmenin sürüm listesi.
 *
 * ### ⚠ TİP `ContractVersionView`, `ContractVersionSummary` DEĞİL
 *
 * Çağıranların bir kısmı bu ucu `ContractVersionSummary[]` diye okuyordu ve o tip
 * `createdBy` (ham kullanıcı kimliği), `contentType`, `sourcePurgedAt`, `retainedUntil`
 * alanlarını ilan ediyor. Sunucu bunların HİÇBİRİNİ döndürmüyor
 * (`apps/api/src/modules/contracts/versions.ts` → `gorunum()`), üstelik bu bilinçli bir
 * gizlilik kararı: *"yetkisiz kullanıcıya gereksiz kişisel bilgi açılmaz"*.
 *
 * Yani o çağıranlar derleyiciye YALAN söylüyordu; o alanlardan biri okunsaydı ekranda
 * sessizce `undefined` çıkardı. Anahtarları birleştirirken tip de doğrusuna çekildi.
 *
 * ⚠ `limit=50` ANAHTARIN PARÇASI: farklı bir limitle çekilen liste farklı bir veridir ve
 * aynı anahtarı paylaşmamalı. Bugün bütün çağıranlar 50 kullanıyor; parametre yine de
 * dışarıda, çünkü anahtarla URL'in birlikte değişmesi gerekiyor.
 */
export function sozlesmeSurumSorgusu(tenantId: string, contractId: string, limit = 50) {
  return queryOptions({
    queryKey: tenantKey(tenantId, "contract", contractId, "surumler", limit),
    queryFn: async () =>
      (
        await api<ContractVersionView[]>(`/api/contracts/${contractId}/versions?limit=${limit}`, {
          tenantId,
        })
      ).data,
  });
}
